import FreshnessNote from '../FreshnessNote';
import React from 'react';
import DetailsToggle from '../DetailsToggle';
import KudosBlock from '../home/KudosBlock';
import { AttentionBlock, LookNextBlock } from '../home/LeadBlocks';
import { AdminHomeData } from '../home/types';
import { useDetails } from '../../hooks/useDetails';
import { FunctionBlock, MonthBlock, PlanBlock, ProjectsBlock } from './TechBlocks';
import { TechData } from './types';

export type TechState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: TechData };

interface Props {
  tech: TechState;
  onRetry: () => void;
  /** The Updates block, built by the page (it loads on its own). */
  updates: React.ReactNode;
  /** An admin also sees the projects furthest from the standards, behind Show details. */
  attention: AdminHomeData | null;
  onGiveKudos: (project: string | null) => void;
  kudosVersion: number;
}

// Home for a leader or an admin: the whole of Tech on one screen. Four blocks: the month so far, the quarter's plan,
// the five functions side by side, and what changed. Everything else (each project, who to thank, what to look at
// next) is one press away, behind Show details.
export default function TechHome({ tech, onRetry, updates, attention, onGiveKudos, kudosVersion }: Props) {
  const [details, setDetails] = useDetails();
  return (
    <>
      {tech.status === 'loading' && <p className="text-sm text-gray-600" role="status">Reading Tech…</p>}
      {tech.status === 'error' && (
        <div role="alert" className="rounded-2xl bg-red-50 p-4 text-sm text-red-800 ring-1 ring-inset ring-red-200">
          {tech.message}{' '}
          <button type="button" onClick={onRetry} className="font-medium underline">Try again</button>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {tech.status === 'ready' && (
          <>
            <MonthBlock tech={tech.data} />
            <PlanBlock tech={tech.data} />
            <FunctionBlock tech={tech.data} />
          </>
        )}
        {updates}
      </div>
      <FreshnessNote />
      <div>
        <DetailsToggle open={details} onChange={setDetails} controls="tech-details" />
      </div>
      {details && (
        <div id="tech-details" className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {tech.status === 'ready' && <ProjectsBlock tech={tech.data} />}
          <LookNextBlock />
          <KudosBlock className="lg:col-span-2" onGive={() => onGiveKudos(null)} reloadKey={kudosVersion} />
          {attention && <AttentionBlock attention={attention.attention} judged={attention.judged} onGiveKudos={(project) => onGiveKudos(project)} />}
        </div>
      )}
    </>
  );
}
